// @flow

import capitalize from 'lodash/capitalize';

import type { DecoratedProduct } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Badge, Base } from '@gandi/ui';

import type { InstanceSize } from 'src/modules/simplehosting/prices';

import { PlanDisplay } from './components/Rows/PlanContent';
import {
  useConfigurationDetailsUpSell,
  useWebhostingPlanPrices,
} from './components/modules/webhosting';

type Props = {
  product: DecoratedProduct,
  size: InstanceSize,
};

export function StudioPlan({ product, size }: Props): React.MixedElement {
  const { __ } = useTranslator();

  const sizeLabel = capitalize(size);

  const { duration } = product.payload;

  const { cpu, storage, mailbox } =
    useConfigurationDetailsUpSell({
      duration: duration ?? 12,
      size,
      process: 'create',
    }) || {};

  const { firstPrice } =
    useWebhostingPlanPrices(
      product?.payload.duration || 1,
      [
        {
          value: size,
          text: sizeLabel,
        },
      ],
      'create',
    ) || {};

  const options = [
    {
      id: 'storage',
      content: storage,
      detail: __(
        'Storage space for your website’s files, images, and content.',
      ),
    },
    {
      id: 'cpu',
      content: cpu,
      detail: __(
        'Resources allocated to keep your website fast and running smoothly.',
      ),
    },
    {
      id: 'mailbox',
      content: mailbox,
      detail: __(
        'Create up to 2 professional email addresses using your domain name.',
      ),
    },
  ];

  const currentDuration = duration
    ? duration >= 12
      ? __('Yearly billing')
      : __('Monthly billing')
    : undefined;

  const label = __('Web hosting: %(sizeLabel)s Plan', { sizeLabel });
  return (
    <PlanDisplay
      isSelected
      sizeLabel={
        <Base flex gap="spacing-4x" flexAlignItemsCenter>
          {label}
          <Badge>{currentDuration}</Badge>
        </Base>
      }
      options={options}
      price={firstPrice}
      durationWording={__(
        '<for-translators-use>Periodicity (month)</for-translators-use>mo',
      )}
    />
  );
}
